// src/components/Student.js
import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { StudentContext } from '../StudentContext';
import {
  fetchAcademicYearsListRest,
  fetchParentStudentDetailRest
} from '../FirebaseService';
import { doc, updateDoc } from 'firebase/firestore';
import { firestore } from '../firebaseConfig';
import './Student.css';

const PAGE_SIZE = 10;

const Student = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { refreshData } = useContext(StudentContext);

  // State Niên khóa
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedYearId, setSelectedYearId] = useState('');

  // State Dữ liệu tổng hợp theo Niên khóa
  const [studentDetail, setStudentDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  // Tabs & Navigation State
  const [activeTab, setActiveTab] = useState('attendance');
  const [attSubTab, setAttSubTab] = useState('church');
  const [visibleChurchCount, setVisibleChurchCount] = useState(PAGE_SIZE);
  const [visibleCatechismCount, setVisibleCatechismCount] = useState(PAGE_SIZE);

  // Edit Mode State - Lưu toàn bộ các trường Firestore
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editFormData, setEditFormData] = useState({
    holyName: '',
    fullName: '',
    gender: 'nam',
    dateOfBirth: '',
    placeOfBirth: '',
    status: 'Còn đi học',
    baptismDate: '',
    baptismPlace: '',
    firstCommunionDate: '',
    confirmationDate: '',
    fatherName: '',
    fatherBirthYear: '',
    fatherJob: '',
    motherName: '',
    motherBirthYear: '',
    motherJob: '',
    address: '',
    subParish: '',
    phone1: '',
    phone2: ''
  });

  // Day Filter State
  const [dayFilters, setDayFilters] = useState({
    'Chúa nhật': true,
    'Thứ hai': false,
    'Thứ ba': false,
    'Thứ tư': false,
    'Thứ năm': false,
    'Thứ sáu': false,
    'Thứ bảy': false
  });

  // 1. TẢI DANH SÁCH NIÊN KHÓA
  useEffect(() => {
    let isMounted = true;
    fetchAcademicYearsListRest()
      .then((years) => {
        if (!isMounted) return;
        if (Array.isArray(years) && years.length > 0) {
          setAcademicYears(years);
          const current = years.find((y) => y.isCurrent) || years[0];
          if (current) setSelectedYearId(current.id);
        } else {
          setErrorMessage('Không thể tải danh sách niên khóa.');
        }
      })
      .catch((err) => {
        console.error('Lỗi khi tải niên khóa:', err);
        if (isMounted) setErrorMessage('Lỗi kết nối máy chủ khi tải niên khóa.');
      });

    return () => { isMounted = false; };
  }, []);

  // 2. TẢI CHI TIẾT HỒ SƠ & TỔNG HỢP DỮ LIỆU
  useEffect(() => {
    if (!studentId || !selectedYearId) return;

    let isMounted = true;
    setIsLoading(true);
    setErrorMessage('');

    fetchParentStudentDetailRest(studentId, selectedYearId)
      .then((res) => {
        if (!isMounted) return;
        if (res.error) {
          setErrorMessage(res.error);
        } else {
          setStudentDetail(res);
          if (res.profile) {
            const p = res.profile;
            setEditFormData({
              holyName: p.holyName || '',
              fullName: p.fullName || '',
              gender: p.gender || 'nam',
              dateOfBirth: p.dateOfBirth || '',
              placeOfBirth: p.placeOfBirth || '',
              status: p.status || 'Còn đi học',
              baptismDate: p.baptismDate || '',
              baptismPlace: p.baptismPlace || '',
              firstCommunionDate: p.firstCommunionDate || '',
              confirmationDate: p.confirmationDate || '',
              fatherName: p.fatherName || '',
              fatherBirthYear: p.fatherBirthYear || '',
              fatherJob: p.fatherJob || '',
              motherName: p.motherName || '',
              motherBirthYear: p.motherBirthYear || '',
              motherJob: p.motherJob || '',
              address: p.address || '',
              subParish: p.subParish || '',
              phone1: p.phone1 || '',
              phone2: p.phone2 || ''
            });
          }
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Lỗi khi tải thông tin thiếu nhi:', err);
        if (isMounted) {
          setErrorMessage('Lỗi hệ thống khi tải thông tin thiếu nhi.');
          setIsLoading(false);
        }
      });

    return () => { isMounted = false; };
  }, [studentId, selectedYearId]);

  const handleDayFilterChange = (day) => {
    setDayFilters(prev => ({ ...prev, [day]: !prev[day] }));
    setVisibleChurchCount(PAGE_SIZE);
    setVisibleCatechismCount(PAGE_SIZE);
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return 'Chưa có ngày';
    const [year, month, day] = dateStr.split('-').map(Number);
    const dateObj = new Date(year, month - 1, day);
    const daysOfWeek = ['Chúa nhật', 'Thứ hai', 'Thứ ba', 'Thứ tư', 'Thứ năm', 'Thứ sáu', 'Thứ bảy'];
    return `${daysOfWeek[dateObj.getDay()]} - ${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
  };

  const formatDateVal = (str) => {
    if (!str) return 'Chưa cập nhật';
    if (str.includes('-')) {
      const [y, m, d] = str.split('-');
      return (y && m && d) ? `${d}/${m}/${y}` : str;
    }
    return str;
  };

  // Lưu chỉnh sửa thông tin Thiếu Nhi vào Firestore
  const handleSaveEdit = async () => {
    setIsSaving(true);
    try {
      const studentRef = doc(firestore, 'students', studentId);
      await updateDoc(studentRef, {
        ...editFormData,
        updatedAt: new Date().toISOString()
      });

      setStudentDetail(prev => prev ? {
        ...prev,
        profile: {
          ...prev.profile,
          ...editFormData
        }
      } : prev);

      if (refreshData) refreshData();
      setIsEditing(false);
      alert('Đã cập nhật đầy đủ thông tin thiếu nhi thành công!');
    } catch (error) {
      console.error('Lỗi khi cập nhật:', error);
      alert('Cập nhật thất bại: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const sortedChurchAttendance = useMemo(() => {
    if (!studentDetail?.churchAttendance) return [];
    return Object.entries(studentDetail.churchAttendance).sort(([a], [b]) => b.localeCompare(a));
  }, [studentDetail]);

  const sortedCatechismAttendance = useMemo(() => {
    if (!studentDetail?.catechismAttendance) return [];
    return Object.entries(studentDetail.catechismAttendance).sort(([a], [b]) => b.localeCompare(a));
  }, [studentDetail]);

  const filterAttendance = (attendanceArr) => {
    const hasActiveFilter = Object.values(dayFilters).some(v => v);
    if (!hasActiveFilter) return attendanceArr;

    return attendanceArr.filter(([dateStr]) => {
      const [year, month, day] = dateStr.split('-').map(Number);
      const dayName = ['Chúa nhật', 'Thứ hai', 'Thứ ba', 'Thứ tư', 'Thứ năm', 'Thứ sáu', 'Thứ bảy'][new Date(year, month - 1, day).getDay()];
      return dayFilters[dayName];
    });
  };

  const filteredChurch = useMemo(() => filterAttendance(sortedChurchAttendance), [sortedChurchAttendance, dayFilters]);
  const filteredCatechism = useMemo(() => filterAttendance(sortedCatechismAttendance), [sortedCatechismAttendance, dayFilters]);

  const getStatusBadge = (statusObj) => {
    const str = String(typeof statusObj === 'object' ? statusObj?.time : statusObj || 'Chưa điểm danh').trim();
    if (str === 'Chưa điểm danh' || str === 'Vắng không xin phép') return { class: 'status-absent', text: 'Vắng mặt' };
    if (str === 'Vắng có xin phép') return { class: 'status-permitted', text: 'Có xin phép' };
    return { class: 'status-present', text: str };
  };

  return (
    <div className="student-wrapper">
      <header className="student-header">
        <button onClick={() => navigate(-1)} className="std-header-back">
          ← Quay lại
        </button>
        <h1 className="std-header-title">Chi Tiết Thiếu Nhi</h1>
        <div className="std-header-placeholder"></div>
      </header>

      <div className="student-container">
        {errorMessage ? (
          <div className="std-error-card">
            <p>❌ {errorMessage}</p>
            <button onClick={() => navigate(-1)} className="std-back-btn">Quay lại</button>
          </div>
        ) : (
          <>
            {/* 🌟 HỒ SƠ THIẾU NHI & CHỈNH SỬA TOÀN BỘ THÔNG TIN */}
            <div className="std-profile-card">
              {isLoading && !studentDetail ? (
                <div className="std-loading-card">
                  <div className="std-spinner"></div>
                  <p>⏳ Đang tải thông tin thiếu nhi...</p>
                </div>
              ) : studentDetail?.profile && (
                <>
                  {!isEditing ? (
                    <>
                      <div className="std-profile-top">
                        <div className="std-profile-avatar-group">
                          <div className="std-avatar-badge">
                            {studentDetail.profile.gender === 'nu' ? '👧' : '👦'}
                          </div>
                          <div className="std-profile-titles">
                            <span className="std-holy-name">{studentDetail.profile.holyName || 'Chưa có tên thánh'}</span>
                            <h2 className="std-full-name">{studentDetail.profile.fullName}</h2>
                          </div>
                        </div>

                        <button className="std-edit-card-btn" onClick={() => setIsEditing(true)}>
                          ✏️ Chỉnh sửa
                        </button>
                      </div>

                      {/* KHỐI 1: THÔNG TIN CƠ BẢN */}
                      <div className="std-section-header">📌 Thông Tin Cơ Bản</div>
                      <div className="std-info-grid">
                        <div className="std-info-item">
                          <span className="info-label">Mã ID</span>
                          <span className="info-value id-highlight">{studentId}</span>
                        </div>
                        <div className="std-info-item">
                          <span className="info-label">Lớp học</span>
                          <span className="info-value">{studentDetail.className}</span>
                        </div>
                        <div className="std-info-item">
                          <span className="info-label">Giới tính</span>
                          <span className="info-value">{studentDetail.profile.gender === 'nu' ? 'Nữ 👧' : 'Nam 👦'}</span>
                        </div>
                        <div className="std-info-item">
                          <span className="info-label">Ngày sinh</span>
                          <span className="info-value">{formatDateVal(studentDetail.profile.dateOfBirth)}</span>
                        </div>
                        <div className="std-info-item">
                          <span className="info-label">Nơi sinh</span>
                          <span className="info-value">{studentDetail.profile.placeOfBirth || 'Chưa cập nhật'}</span>
                        </div>
                        <div className="std-info-item">
                          <span className="info-label">Trạng thái</span>
                          <span className={`std-status-pill ${studentDetail.profile.status === 'Nghỉ luôn' ? 'status-off' : 'status-on'}`}>
                            ● {studentDetail.profile.status || 'Còn đi học'}
                          </span>
                        </div>
                      </div>

                      {/* KHỐI 2: THÔNG TIN BÍ TÍCH */}
                      <div className="std-section-header">🕊️ Thông Tin Bí Tích</div>
                      <div className="std-info-grid">
                        <div className="std-info-item">
                          <span className="info-label">Ngày Rửa Tội</span>
                          <span className="info-value">{formatDateVal(studentDetail.profile.baptismDate)}</span>
                        </div>
                        <div className="std-info-item">
                          <span className="info-label">Nơi Rửa Tội</span>
                          <span className="info-value">{studentDetail.profile.baptismPlace || 'Chưa cập nhật'}</span>
                        </div>
                        <div className="std-info-item">
                          <span className="info-label">Ngày Rước Lễ</span>
                          <span className="info-value">{formatDateVal(studentDetail.profile.firstCommunionDate)}</span>
                        </div>
                        <div className="std-info-item">
                          <span className="info-label">Ngày Thêm Sức</span>
                          <span className="info-value">{formatDateVal(studentDetail.profile.confirmationDate)}</span>
                        </div>
                      </div>

                      {/* KHỐI 3: THÔNG TIN GIA ĐÌNH */}
                      <div className="std-section-header">👨‍👩‍👧 Thông Tin Gia Đình</div>
                      <div className="std-info-grid">
                        <div className="std-info-item">
                          <span className="info-label">Tên Cha</span>
                          <span className="info-value">{studentDetail.profile.fatherName || 'Chưa cập nhật'}</span>
                        </div>
                        <div className="std-info-item">
                          <span className="info-label">Năm sinh / Nghề nghiệp Cha</span>
                          <span className="info-value">
                            {studentDetail.profile.fatherBirthYear || '---'} {studentDetail.profile.fatherJob ? `(${studentDetail.profile.fatherJob})` : ''}
                          </span>
                        </div>
                        <div className="std-info-item">
                          <span className="info-label">Tên Mẹ</span>
                          <span className="info-value">{studentDetail.profile.motherName || 'Chưa cập nhật'}</span>
                        </div>
                        <div className="std-info-item">
                          <span className="info-label">Năm sinh / Nghề nghiệp Mẹ</span>
                          <span className="info-value">
                            {studentDetail.profile.motherBirthYear || '---'} {studentDetail.profile.motherJob ? `(${studentDetail.profile.motherJob})` : ''}
                          </span>
                        </div>
                      </div>

                      {/* KHỐI 4: LIÊN HỆ & ĐỊA CHỈ */}
                      <div className="std-section-header">🏠 Liên Hệ & Địa Chỉ</div>
                      <div className="std-info-grid">
                        <div className="std-info-item span-full">
                          <span className="info-label">Địa chỉ</span>
                          <span className="info-value">{studentDetail.profile.address || 'Chưa cập nhật'}</span>
                        </div>
                        <div className="std-info-item">
                          <span className="info-label">Giáo khu</span>
                          <span className="info-value">{studentDetail.profile.subParish || 'Chưa cập nhật'}</span>
                        </div>
                        <div className="std-info-item">
                          <span className="info-label">Điện thoại 1 / 2</span>
                          <span className="info-value">
                            {studentDetail.profile.phone1 || '---'} {studentDetail.profile.phone2 ? `/ ${studentDetail.profile.phone2}` : ''}
                          </span>
                        </div>
                      </div>

                      {/* BỘ CHỌN NIÊN KHÓA */}
                      <div className="std-year-selector-box">
                        <label>Niên khóa xem dữ liệu:</label>
                        <select
                          value={selectedYearId}
                          onChange={(e) => setSelectedYearId(e.target.value)}
                          className="std-year-select"
                        >
                          {academicYears.map((y) => (
                            <option key={y.id} value={y.id}>
                              {y.name} {y.isCurrent ? '(Hiện tại)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  ) : (
                    /* FORM CHỈNH SỬA TOÀN BỘ SƠ YẾU LÝ LỊCH */
                    <div className="std-edit-form">
                      <h3>✏️ Chỉnh Sửa Sơ Yếu Lý Lịch</h3>

                      {/* BỔ SUNG 1: CƠ BẢN */}
                      <div className="std-form-section-title">📌 Thông Tin Cơ Bản</div>
                      <div className="edit-form-grid">
                        <div className="form-group">
                          <label>Tên Thánh:</label>
                          <input
                            type="text"
                            value={editFormData.holyName}
                            onChange={(e) => setEditFormData({ ...editFormData, holyName: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Họ và Tên:</label>
                          <input
                            type="text"
                            value={editFormData.fullName}
                            onChange={(e) => setEditFormData({ ...editFormData, fullName: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Giới tính:</label>
                          <select
                            value={editFormData.gender}
                            onChange={(e) => setEditFormData({ ...editFormData, gender: e.target.value })}
                          >
                            <option value="nam">Nam 👦</option>
                            <option value="nu">Nữ 👧</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Ngày sinh:</label>
                          <input
                            type="text"
                            placeholder="DD/MM/YYYY"
                            value={editFormData.dateOfBirth}
                            onChange={(e) => setEditFormData({ ...editFormData, dateOfBirth: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Nơi sinh:</label>
                          <input
                            type="text"
                            value={editFormData.placeOfBirth}
                            onChange={(e) => setEditFormData({ ...editFormData, placeOfBirth: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Trạng thái đi học:</label>
                          <select
                            value={editFormData.status}
                            onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                          >
                            <option value="Còn đi học">Còn đi học</option>
                            <option value="Tạm nghỉ">Tạm nghỉ</option>
                            <option value="Nghỉ luôn">Nghỉ luôn</option>
                          </select>
                        </div>
                      </div>

                      {/* BỔ SUNG 2: BÍ TÍCH */}
                      <div className="std-form-section-title">🕊️ Thông Tin Bí Tích</div>
                      <div className="edit-form-grid">
                        <div className="form-group">
                          <label>Ngày Rửa Tội:</label>
                          <input
                            type="text"
                            placeholder="DD/MM/YYYY"
                            value={editFormData.baptismDate}
                            onChange={(e) => setEditFormData({ ...editFormData, baptismDate: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Nơi Rửa Tội:</label>
                          <input
                            type="text"
                            value={editFormData.baptismPlace}
                            onChange={(e) => setEditFormData({ ...editFormData, baptismPlace: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Ngày Rước Lễ:</label>
                          <input
                            type="text"
                            placeholder="DD/MM/YYYY"
                            value={editFormData.firstCommunionDate}
                            onChange={(e) => setEditFormData({ ...editFormData, firstCommunionDate: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Ngày Thêm Sức:</label>
                          <input
                            type="text"
                            placeholder="DD/MM/YYYY"
                            value={editFormData.confirmationDate}
                            onChange={(e) => setEditFormData({ ...editFormData, confirmationDate: e.target.value })}
                          />
                        </div>
                      </div>

                      {/* BỔ SUNG 3: GIA ĐÌNH */}
                      <div className="std-form-section-title">👨‍👩‍👧 Thông Tin Gia Đình</div>
                      <div className="edit-form-grid">
                        <div className="form-group">
                          <label>Tên Cha:</label>
                          <input
                            type="text"
                            value={editFormData.fatherName}
                            onChange={(e) => setEditFormData({ ...editFormData, fatherName: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Năm sinh Cha:</label>
                          <input
                            type="text"
                            value={editFormData.fatherBirthYear}
                            onChange={(e) => setEditFormData({ ...editFormData, fatherBirthYear: e.target.value })}
                          />
                        </div>
                        <div className="form-group span-full">
                          <label>Nghề nghiệp Cha:</label>
                          <input
                            type="text"
                            value={editFormData.fatherJob}
                            onChange={(e) => setEditFormData({ ...editFormData, fatherJob: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Tên Mẹ:</label>
                          <input
                            type="text"
                            value={editFormData.motherName}
                            onChange={(e) => setEditFormData({ ...editFormData, motherName: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Năm sinh Mẹ:</label>
                          <input
                            type="text"
                            value={editFormData.motherBirthYear}
                            onChange={(e) => setEditFormData({ ...editFormData, motherBirthYear: e.target.value })}
                          />
                        </div>
                        <div className="form-group span-full">
                          <label>Nghề nghiệp Mẹ:</label>
                          <input
                            type="text"
                            value={editFormData.motherJob}
                            onChange={(e) => setEditFormData({ ...editFormData, motherJob: e.target.value })}
                          />
                        </div>
                      </div>

                      {/* BỔ SUNG 4: LIÊN HỆ & ĐỊA CHỈ */}
                      <div className="std-form-section-title">🏠 Liên Hệ & Địa Chỉ</div>
                      <div className="edit-form-grid">
                        <div className="form-group span-full">
                          <label>Địa chỉ nhà:</label>
                          <input
                            type="text"
                            value={editFormData.address}
                            onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Giáo khu:</label>
                          <input
                            type="text"
                            value={editFormData.subParish}
                            onChange={(e) => setEditFormData({ ...editFormData, subParish: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Điện thoại 1:</label>
                          <input
                            type="text"
                            value={editFormData.phone1}
                            onChange={(e) => setEditFormData({ ...editFormData, phone1: e.target.value })}
                          />
                        </div>
                        <div className="form-group span-full">
                          <label>Điện thoại 2:</label>
                          <input
                            type="text"
                            value={editFormData.phone2}
                            onChange={(e) => setEditFormData({ ...editFormData, phone2: e.target.value })}
                          />
                        </div>
                      </div>

                      <div className="edit-actions">
                        <button className="btn-cancel" onClick={() => setIsEditing(false)}>Hủy bỏ</button>
                        <button className="btn-save" onClick={handleSaveEdit} disabled={isSaving}>
                          {isSaving ? 'Đang lưu...' : 'Lưu tất cả thay đổi'}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* 🌟 TABS ĐIỀU HƯỚNG */}
            <div className="std-tabs">
              <button className={`std-tab-btn ${activeTab === 'attendance' ? 'active' : ''}`} onClick={() => setActiveTab('attendance')}>⛪ Điểm Danh</button>
              <button className={`std-tab-btn ${activeTab === 'transcript' ? 'active' : ''}`} onClick={() => setActiveTab('transcript')}>📊 Bảng Điểm</button>
              <button className={`std-tab-btn ${activeTab === 'qrcode' ? 'active' : ''}`} onClick={() => setActiveTab('qrcode')}>🪪 Thẻ QR</button>
            </div>

            {/* TAB ĐIỂM DANH */}
            {activeTab === 'attendance' && (
              <div className="std-tab-content">
                <div className="std-sub-tabs">
                  <button className={`std-subtab-btn ${attSubTab === 'church' ? 'active' : ''}`} onClick={() => setAttSubTab('church')}>⛪ Đi Lễ Chúa Nhật</button>
                  <button className={`std-subtab-btn catechism ${attSubTab === 'catechism' ? 'active' : ''}`} onClick={() => setAttSubTab('catechism')}>📖 Học Giáo Lý</button>
                </div>

                <div className="std-filter-row">
                  <span className="filter-label">Lọc theo ngày trong tuần:</span>
                  <div className="filter-chips">
                    {Object.keys(dayFilters).map(day => (
                      <button
                        key={day}
                        className={`filter-chip ${dayFilters[day] ? 'selected' : ''}`}
                        onClick={() => handleDayFilterChange(day)}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>

                {isLoading ? (
                  <div className="std-loading-card">
                    <div className="std-spinner"></div>
                    <p>⏳ Đang tải nhật ký điểm danh...</p>
                  </div>
                ) : (
                  <>
                    {attSubTab === 'church' && (
                      <div className="std-section-box">
                        <h3>⛪ Lịch Sử Đi Lễ</h3>
                        <div className="std-att-list">
                          {filteredChurch.length > 0 ? (
                            <>
                              {filteredChurch.slice(0, visibleChurchCount).map(([date, status]) => {
                                const badge = getStatusBadge(status);
                                return (
                                  <div key={date} className="std-att-item">
                                    <span className="att-date">{formatDisplayDate(date)}</span>
                                    <span className={`att-status ${badge.class}`}>{badge.text}</span>
                                  </div>
                                );
                              })}
                              {visibleChurchCount < filteredChurch.length && (
                                <button className="std-load-more-btn" onClick={() => setVisibleChurchCount(p => p + PAGE_SIZE)}>
                                  🔽 Xem thêm ngày cũ hơn ({filteredChurch.length - visibleChurchCount})
                                </button>
                              )}
                            </>
                          ) : (
                            <p className="std-empty-text">Chưa có nhật ký đi lễ trong niên khóa này.</p>
                          )}
                        </div>
                      </div>
                    )}

                    {attSubTab === 'catechism' && (
                      <div className="std-section-box">
                        <h3>📖 Lịch Sử Học Giáo Lý</h3>
                        <div className="std-att-list">
                          {filteredCatechism.length > 0 ? (
                            <>
                              {filteredCatechism.slice(0, visibleCatechismCount).map(([date, status]) => {
                                const badge = getStatusBadge(status);
                                return (
                                  <div key={date} className="std-att-item">
                                    <span className="att-date">{formatDisplayDate(date)}</span>
                                    <span className={`att-status ${badge.class}`}>{badge.text}</span>
                                  </div>
                                );
                              })}
                              {visibleCatechismCount < filteredCatechism.length && (
                                <button className="std-load-more-btn" onClick={() => setVisibleCatechismCount(p => p + PAGE_SIZE)}>
                                  🔽 Xem thêm ngày cũ hơn ({filteredCatechism.length - visibleCatechismCount})
                                </button>
                              )}
                            </>
                          ) : (
                            <p className="std-empty-text">Chưa có nhật ký học giáo lý trong niên khóa này.</p>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* TAB BẢNG ĐIỂM */}
            {activeTab === 'transcript' && (
              <div className="std-tab-content">
                {isLoading ? (
                  <div className="std-loading-card">
                    <div className="std-spinner"></div>
                    <p>⏳ Đang tải bảng điểm...</p>
                  </div>
                ) : studentDetail?.transcript ? (
                  <div className="std-scores-grid">
                    <div className="std-score-card">
                      <h4>📘 Học Kỳ I</h4>
                      <div className="score-rows">
                        <div className="score-row"><span>Miệng:</span> <strong>{studentDetail.transcript.semester1?.oral ?? '-'}</strong></div>
                        <div className="score-row"><span>15 phút:</span> <strong>{studentDetail.transcript.semester1?.fifteenMinutes ?? '-'}</strong></div>
                        <div className="score-row"><span>1 tiết:</span> <strong>{studentDetail.transcript.semester1?.onePeriod ?? '-'}</strong></div>
                        <div className="score-row"><span>Thi:</span> <strong>{studentDetail.transcript.semester1?.exam ?? '-'}</strong></div>
                        <div className="score-row highlight"><span>Trung bình HK1:</span> <strong>{studentDetail.transcript.semester1?.average ?? '-'}</strong></div>
                      </div>
                    </div>

                    <div className="std-score-card">
                      <h4>📗 Học Kỳ II</h4>
                      <div className="score-rows">
                        <div className="score-row"><span>Miệng:</span> <strong>{studentDetail.transcript.semester2?.oral ?? '-'}</strong></div>
                        <div className="score-row"><span>15 phút:</span> <strong>{studentDetail.transcript.semester2?.fifteenMinutes ?? '-'}</strong></div>
                        <div className="score-row"><span>1 tiết:</span> <strong>{studentDetail.transcript.semester2?.onePeriod ?? '-'}</strong></div>
                        <div className="score-row"><span>Thi:</span> <strong>{studentDetail.transcript.semester2?.exam ?? '-'}</strong></div>
                        <div className="score-row highlight"><span>Trung bình HK2:</span> <strong>{studentDetail.transcript.semester2?.average ?? '-'}</strong></div>
                      </div>
                    </div>

                    <div className="std-score-card yearly">
                      <h4>🏆 Cả Năm</h4>
                      <div className="score-rows">
                        <div className="score-row highlight-final"><span>Trung Bình Cả Năm:</span> <strong>{studentDetail.transcript.yearly?.average ?? '-'}</strong></div>
                        <div className="score-row"><span>Thi vấn đáp:</span> <strong>{studentDetail.transcript.yearly?.oralExam ?? '-'}</strong></div>
                        <div className="score-row"><span>Chuyên cần:</span> <strong>{studentDetail.transcript.yearly?.diligence ?? '-'}</strong></div>
                        <div className="score-row"><span>Xếp hạng:</span> <strong>{studentDetail.transcript.yearly?.rank ?? '-'}</strong></div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="std-section-box"><p className="std-empty-text">Chưa có dữ liệu bảng điểm cho niên khóa này.</p></div>
                )}
              </div>
            )}

            {/* TAB THẺ QR */}
            {activeTab === 'qrcode' && (
              <div className="std-tab-content center-tab">
                <div className="std-qr-card">
                  <h3>Mã QR Điểm Danh</h3>
                  <p>Sử dụng mã QR này để điểm danh tự động</p>
                  {studentId ? (
                    <div className="std-qr-box">
                      <QRCodeSVG
                        value={studentId}
                        size={180}
                        level="M"
                        className="std-qr-image"
                      />
                    </div>
                  ) : (
                    <p className="std-empty-text">Không có QR</p>
                  )}
                  <span className="std-qr-student-id">{studentId}</span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Student;